/**
 * Context-aware LexCounsel suggestions — screen-focused, no slash orchestration.
 */

import type { LexCounselSendScope } from './lexCounselSend';
import type { LexCounselDashboardBrief } from './lexCounselDashboardBrief';
import type { EditorCounselMode, LexCounselWorkspaceSnapshot } from '../store/lexCounselStore';

export interface LexCounselSuggestion {
  id: string;
  label: string;
  prompt: string;
  scope?: LexCounselSendScope;
  /** Draft-editor chip: sets mode before populate (does not auto-send). */
  editorCounselMode?: EditorCounselMode;
  /** When true, clicking the chip immediately sends (used for Run Filing Review). */
  autoSend?: boolean;
  /** Marks the primary filing-review CTA. */
  primary?: boolean;
  /** Run deterministic evaluate API before/alongside the LLM send. */
  runFilingReview?: boolean;
}

export const FIX_DOCUMENT_NUMBERING_PROMPT = `Fix all paragraph numbering in this document. Apply Pakistani pleading numbering rules:
- Restart plaintiffs/applicants/petitioners at 1., 2., 3. …
- After VERSUS, restart defendants/respondents at 1., 2., 3. …
- Restart body averments at 1. That …, 2. That …
- Restart prayer clauses at (a), (b), (c) or 1., 2., 3. — never continue body numbers into prayer
Preserve all existing text, party names, and formatting. Only correct numbering sequences.`;

function daysUntil(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function hearingWhenLabel(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days}d`;
}

function dashboardSuggestions(brief?: LexCounselDashboardBrief): LexCounselSuggestion[] {
  const out: LexCounselSuggestion[] = [];

  if (brief) {
    const nearest = brief.hearings[0];
    if (nearest) {
      const when = hearingWhenLabel(nearest.daysUntil);
      out.push({
        id: 'hearing-nearest',
        label: `Prepare for ${nearest.title.slice(0, 26)}${nearest.title.length > 26 ? '…' : ''} (${when})`,
        prompt: `What should I prepare for the hearing in ${nearest.title}?`,
      });
    }

    if (brief.pendingReminders > 0) {
      out.push({
        id: 'reminders',
        label: `${brief.pendingReminders} pending reminder${brief.pendingReminders === 1 ? '' : 's'}`,
        prompt: 'What pending reminders need my attention?',
      });
    }

    if (brief.openDrafts > 0) {
      out.push({
        id: 'drafts',
        label: `${brief.openDrafts} open draft${brief.openDrafts === 1 ? '' : 's'}`,
        prompt: 'Which drafts should I continue working on?',
      });
    }
  }

  out.push({
    id: 'brief',
    label: "Today's priorities",
    prompt: 'What should I prioritize today?',
  });

  return out;
}

function draftEditorAskSuggestions(): LexCounselSuggestion[] {
  return [
    {
      id: 'explain-clause',
      label: 'Explain this clause',
      prompt: 'Explain this clause in plain language',
      editorCounselMode: 'ask',
    },
    {
      id: 'procedure',
      label: "What's the procedure for this?",
      prompt: "What's the procedure for this?",
      editorCounselMode: 'ask',
    },
    {
      id: 'summarize-doc',
      label: 'Summarize this document',
      prompt: 'Summarize this document',
      editorCounselMode: 'ask',
    },
  ];
}

function draftEditorDraftSuggestions(): LexCounselSuggestion[] {
  return [
    {
      id: 'redraft-para',
      label: 'Redraft this paragraph',
      prompt: 'Redraft this paragraph for clarity and legal precision',
      editorCounselMode: 'draft',
    },
    {
      id: 'prayer',
      label: 'Add a prayer clause',
      prompt: 'Add a prayer clause for this document',
      editorCounselMode: 'draft',
    },
    {
      id: 'fix-numbering',
      label: 'Fix numbering',
      prompt: FIX_DOCUMENT_NUMBERING_PROMPT,
      scope: 'document',
      editorCounselMode: 'draft',
    },
  ];
}

function draftEditorReviewSuggestions(hasFilingReview: boolean): LexCounselSuggestion[] {
  const primary: LexCounselSuggestion = {
    id: 'run-filing-review',
    label: 'Run Filing Review',
    prompt:
      'Run a complete One-Click Filing Review of this document as a senior Pakistani litigation advocate specialising in this case type. Use the deterministic filing-package findings and focus on legal strength, maintainability, jurisdiction, limitation, prayer, annexures, accompaniments (affidavit, memo of address, vakalatnama, index, court fee), and litigation risk. Do not give generic advice.',
    editorCounselMode: 'review',
    autoSend: true,
    primary: true,
    runFilingReview: true,
    scope: 'document',
  };

  if (!hasFilingReview) {
    return [primary];
  }

  return [
    primary,
    {
      id: 'recheck-annexures',
      label: 'Re-check annexures',
      prompt: 'Re-check annexure references and missing annexures across all tabs against the filing package.',
      editorCounselMode: 'review',
      autoSend: true,
      runFilingReview: true,
    },
    {
      id: 'explain-critical',
      label: 'Explain critical issues',
      prompt: 'Explain the critical filing-review issues and the practical order in which to fix them before filing.',
      editorCounselMode: 'review',
      autoSend: true,
    },
  ];
}

export function getLexCounselSuggestions(
  snapshot: LexCounselWorkspaceSnapshot,
  editorCounselMode: EditorCounselMode = 'draft',
  options?: { hasFilingReview?: boolean },
): LexCounselSuggestion[] {
  const screen = snapshot.screen;

  if (screen === 'draft_editor') {
    switch (editorCounselMode) {
      case 'draft':
        return draftEditorDraftSuggestions();
      case 'review':
        return draftEditorReviewSuggestions(Boolean(options?.hasFilingReview));
      default:
        return draftEditorAskSuggestions();
    }
  }

  if (screen === 'case') {
    const out: LexCounselSuggestion[] = [
      { id: 'summarize', label: 'Summarize case', prompt: 'Summarize the key facts and current posture of this case' },
      { id: 'next', label: 'Next steps', prompt: 'What are the logical next steps in this matter?' },
    ];
    const days = daysUntil(snapshot.nextHearingDate);
    if (days !== null && days >= 0 && days <= 14) {
      out.unshift({
        id: 'hearing',
        label: days === 0 ? "Today's hearing" : `Hearing in ${days}d`,
        prompt: 'What should I know before the upcoming hearing?',
      });
    }
    return out.slice(0, 4);
  }

  if (screen === 'dashboard') {
    return dashboardSuggestions(snapshot.dashboardBrief).slice(0, 4);
  }

  if (screen === 'calendar') {
    return [
      { id: 'schedule', label: 'This week', prompt: "What's on my schedule this week?" },
      { id: 'hearings', label: 'Upcoming hearings', prompt: 'List my upcoming hearings' },
      { id: 'deadlines', label: 'Deadlines', prompt: 'What deadlines need attention?' },
    ];
  }

  if (screen === 'templates' || screen === 'settings') {
    return [
      { id: 'features', label: 'LexGat features', prompt: 'What can Lex Counsel help me with in LexGat?' },
      { id: 'case', label: 'Create a case', prompt: 'How do I create or import a case in LexGat?' },
      { id: 'draft', label: 'Start a draft', prompt: 'How do I start a new draft document?' },
    ];
  }

  return [
    { id: 'help', label: 'What can Lex Counsel do?', prompt: 'What can you help me with in LexGat?' },
  ];
}
